import { useCallback, useEffect, useLayoutEffect, useState, RefObject } from 'react';
import useResizeObserver from '@creditflowresearch/uilib/hooks/useResizeObserver';

import { PANEL_TIMINGS } from '@creditflowresearch/uilib/PanelControl/timings';

/**
 * Keeps the shared viewport's offset in sync with the trigger row it hangs from, via the
 * `--activator-top` / `--activator-height` custom properties the panel is positioned with.
 *
 * The row sits in a sticky wrapper below a banner that mounts late and scrolls away again, so its
 * position changes while its size does not — which a `ResizeObserver` never reports. Measuring as
 * the panel opens covers that; the observer covers genuine resizes of the row itself.
 */
const usePanelPosition = (
  containerRef: RefObject<HTMLDivElement | null>,
  isPanelOpen: boolean,
): void => {
  // The uikit observer takes an element rather than a ref, so mirror it once attached
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setContainer(containerRef.current);
  }, [containerRef]);

  const updatePosition = useCallback((): void => {
    const element = containerRef.current;
    if (!element) return;

    const { top, height } = element.getBoundingClientRect();
    element.style.setProperty('--activator-top', `${ top }px`);
    element.style.setProperty('--activator-height', `${ height }px`);
    element.style.setProperty('--panel-animation-duration', `${ PANEL_TIMINGS.animationDuration }ms`);
  }, [containerRef]);

  useResizeObserver(container, updatePosition);

  useLayoutEffect(() => {
    if (isPanelOpen) {
      updatePosition();
    }
  }, [isPanelOpen, updatePosition]);
};

export default usePanelPosition;
